import { supabase } from '../../lib/supabase';
export class SalesFollowUpsService {
    /**
     * Get all follow-ups for an organization
     */
    async getFollowUps(organizationId) {
        try {
            const { data, error } = await supabase
                .from('sales_follow_ups')
                .select('*')
                .eq('organization_id', organizationId)
                .order('due_date', { ascending: true });
            if (error) {
                console.error('Error fetching follow-ups:', error);
                throw error;
            }
            return this.transformFollowUps(data || []);
        }
        catch (error) {
            console.error('Error in getFollowUps:', error);
            return [];
        }
    }
    /**
     * Get pending follow-ups for a user
     */
    async getPendingFollowUps(organizationId, userId) {
        try {
            const { data, error } = await supabase
                .from('sales_follow_ups')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('assigned_to', userId)
                .eq('status', 'pending')
                .order('due_date', { ascending: true });
            if (error) {
                console.error('Error fetching pending follow-ups:', error);
                return [];
            }
            return this.transformFollowUps(data || []);
        }
        catch (error) {
            console.error('Error in getPendingFollowUps:', error);
            return [];
        }
    }
    /**
     * Get overdue follow-ups
     */
    async getOverdueFollowUps(organizationId) {
        try {
            const { data, error } = await supabase
                .from('sales_follow_ups')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('status', 'overdue')
                .order('due_date', { ascending: true });
            if (error) {
                console.error('Error fetching overdue follow-ups:', error);
                return [];
            }
            return this.transformFollowUps(data || []);
        }
        catch (error) {
            console.error('Error in getOverdueFollowUps:', error);
            return [];
        }
    }
    /**
     * Get upcoming follow-ups (next 7 days)
     */
    async getUpcomingFollowUps(organizationId, days = 7) {
        try {
            const today = new Date();
            const futureDate = new Date();
            futureDate.setDate(today.getDate() + days);
            const { data, error } = await supabase
                .from('sales_follow_ups')
                .select('*')
                .eq('organization_id', organizationId)
                .in('status', ['pending', 'overdue'])
                .gte('due_date', today.toISOString())
                .lte('due_date', futureDate.toISOString())
                .order('due_date', { ascending: true });
            if (error) {
                console.error('Error fetching upcoming follow-ups:', error);
                return [];
            }
            return this.transformFollowUps(data || []);
        }
        catch (error) {
            console.error('Error in getUpcomingFollowUps:', error);
            return [];
        }
    }
    /**
     * Create a new follow-up
     */
    async createFollowUp(organizationId, userId, request) {
        try {
            const { data, error } = await supabase
                .from('sales_follow_ups')
                .insert({
                organization_id: organizationId,
                contact_id: request.contactId,
                deal_id: request.dealId,
                title: request.title,
                follow_up_type: request.followUpType,
                due_date: request.dueDate.toISOString(),
                priority: request.priority,
                status: 'pending',
                created_by: userId,
                assigned_to: userId
            })
                .select()
                .single();
            if (error) {
                console.error('Error creating follow-up:', error);
                throw error;
            }
            return this.transformFollowUp(data);
        }
        catch (error) {
            console.error('Error in createFollowUp:', error);
            return null;
        }
    }
    /**
     * Complete a follow-up
     */
    async completeFollowUp(followUpId) {
        try {
            const { error } = await supabase
                .from('sales_follow_ups')
                .update({
                status: 'completed',
                completed_at: new Date().toISOString()
            })
                .eq('id', followUpId);
            if (error) {
                console.error('Error completing follow-up:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in completeFollowUp:', error);
            return false;
        }
    }
    /**
     * Cancel a follow-up
     */
    async cancelFollowUp(followUpId) {
        try {
            const { error } = await supabase
                .from('sales_follow_ups')
                .update({ status: 'cancelled' })
                .eq('id', followUpId);
            if (error) {
                console.error('Error cancelling follow-up:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in cancelFollowUp:', error);
            return false;
        }
    }
    /**
     * Update follow-up due date
     */
    async updateDueDate(followUpId, newDueDate) {
        try {
            const { error } = await supabase
                .from('sales_follow_ups')
                .update({ due_date: newDueDate.toISOString() })
                .eq('id', followUpId);
            if (error) {
                console.error('Error updating due date:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in updateDueDate:', error);
            return false;
        }
    }
    /**
     * Subscribe to real-time follow-up changes
     */
    subscribeToFollowUps(organizationId, callback) {
        return supabase
            .channel(`sales_follow_ups:${organizationId}`)
            .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'sales_follow_ups',
            filter: `organization_id=eq.${organizationId}`
        }, (payload) => {
            if (payload.new) {
                callback(this.transformFollowUp(payload.new));
            }
        })
            .subscribe();
    }
    /**
     * Transform database row to SalesFollowUp type
     */
    transformFollowUp(row) {
        return {
            id: row.id,
            organizationId: row.organization_id,
            contactId: row.contact_id,
            dealId: row.deal_id,
            title: row.title,
            description: row.description,
            followUpType: row.follow_up_type,
            dueDate: new Date(row.due_date),
            completedAt: row.completed_at ? new Date(row.completed_at) : undefined,
            priority: row.priority,
            status: row.status,
            assignedTo: row.assigned_to,
            createdBy: row.created_by,
            notes: row.notes,
            createdAt: new Date(row.created_at),
            updatedAt: new Date(row.updated_at)
        };
    }
    /**
     * Transform multiple database rows
     */
    transformFollowUps(rows) {
        return rows.map(row => this.transformFollowUp(row));
    }
}
// Export singleton instance
export const salesFollowUpsService = new SalesFollowUpsService();
